// Tool permission editor — checkboxes for every tool in TOOL_REGISTRY,
// Save/Cancel buttons. Used inside UserManagement's expanded row.
import { useState } from "react";
import { TOOL_REGISTRY } from "../../../../shared/tools/registry";

interface Props {
  userId: number;
  initialToolIds: string[];
  onSave: (toolIds: string[]) => Promise<void>;
  onCancel: () => void;
}

export function ToolPermissionEditor({
  userId,
  initialToolIds,
  onSave,
  onCancel,
}: Props) {
  const [selected, setSelected] = useState<Set<string>>(
    () => new Set(initialToolIds),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      await onSave(Array.from(selected));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        background: "var(--bsp-surface-muted)",
        padding: 16,
        borderRadius: 6,
        marginTop: 8,
      }}
    >
      <div className="bsp-label" style={{ marginBottom: 10 }}>
        Tool permissions for user #{userId}
      </div>
      <div style={{ display: "grid", gap: 8 }}>
        {TOOL_REGISTRY.map((tool) => (
          <label
            key={tool.id}
            style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
          >
            <input
              type="checkbox"
              checked={selected.has(tool.id)}
              onChange={() => toggle(tool.id)}
              disabled={saving}
            />
            <span style={{ fontWeight: 600 }}>{tool.name}</span>
            <span style={{ color: "var(--bsp-fg-muted)", fontSize: 12 }}>
              {tool.id} • {tool.status}
            </span>
          </label>
        ))}
      </div>
      {error && (
        <p style={{ color: "var(--bsp-red-fg)", marginTop: 8, fontSize: 13 }}>
          {error}
        </p>
      )}
      <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
        <button
          type="button"
          className="bsp-btn bsp-btn-primary bsp-btn-sm"
          onClick={() => void save()}
          disabled={saving}
        >
          {saving ? "Saving…" : "Save"}
        </button>
        <button
          type="button"
          className="bsp-btn bsp-btn-secondary bsp-btn-sm"
          onClick={onCancel}
          disabled={saving}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
